import { ReactNode, useRef, useState } from 'react';

import CardStack from '../../../components/CardStack/CardStack';
import Form, { FormHandles } from '../../../components/Form/Form';
import ScrollToLastMounted from '../../../components/ScrollToLastMounted/ScrollToLastMounted';
import Stack from '../../../components/Stack/Stack';
import Title from '../../../components/Title/Title';
import UncaughtErrors from '../../../components/UncaughtErrors/UncaughtErrors';
import useIsMobile from '../../../hooks/ui/useIsMobile';
import useTrans from '../../../hooks/ui/useTrans';
import FormDimmableCard from '../../components/FormDimmableCard/FormDimmableCard';
import usePreview from '../../hooks/usePreview';
import DockWithMedia from '../../types/DockWithMedia';

import BookingParameterFields from './components/BookingParameterFields/BookingParameterFields';
import ListOfDocks from './components/ListOfDocks/ListOfDocks';
import PickupDateCard from './components/PickupDateCard/PickupDateCard';
import determineIfCustomerWantsMultipleDays from './helpers/determineIfCustomerWantsMultipleDays';
import sortedAndFilteredDocks from './helpers/sortedAndFilteredDocks';
import useFetchDates from './hooks/useFetchDates';
import useRequestedDates from './hooks/useRequestedDates';
import BookingFormLayout from './layouts/BookingFormLayout';
import ComposeBookingLayout from './layouts/ComposeBookingLayout';
import ComposeBookingProps from './types/pageProps/ComposeBookingProps';

type DockViewProps = {
    docks: Record<string, DockWithMedia>;
    googleApiKey: string;
} & ComposeBookingProps;

const DockView = ({
    docks,
    googleApiKey,
    parameters = {},
    nextStepUrl,
    uiParameters,
    errors,
}: DockViewProps) => {
    const trans = useTrans('CustomerArea.BookingForm.Dock.');
    const [dimCards, setDimCards] = useState(false);
    const preview = usePreview<DockWithMedia>({});
    const isMobile = useIsMobile();
    const formRef = useRef<FormHandles>(null);

    const fetchDates = useFetchDates(nextStepUrl, parameters);
    const requestedDates = useRequestedDates(parameters);

    const sortedDocks = sortedAndFilteredDocks(docks, uiParameters);
    const customerWantsMultipleDays =
        determineIfCustomerWantsMultipleDays(requestedDates);

    return (
        <Form
            method="get"
            action={nextStepUrl}
            formRef={formRef}
            inertiaOptions={{ preserveScroll: true }}
        >
            <BookingParameterFields parameters={parameters} />

            <ScrollToLastMounted offset={-50}>
                <CardStack noDecoration>
                    <PickupDateCard
                        isFirst={!isMobile}
                        value={requestedDates}
                        onChange={fetchDates}
                        dimmed={preview.isPreviewing}
                        onInteractingWithDatePicker={setDimCards}
                    />

                    <FormDimmableCard dimmed={preview.isPreviewing || dimCards}>
                        {requestedDates?.pickupDate && (
                            <ScrollToLastMounted.Point
                                deps={[
                                    requestedDates?.pickupDate,
                                    requestedDates?.returnDate,
                                ]}
                            />
                        )}

                        <Stack space="xlarge">
                            <Title variant="area">{trans('Title')}</Title>

                            <ListOfDocks
                                docks={sortedDocks}
                                googleApiKey={googleApiKey}
                                customerWantsMultipleDays={
                                    customerWantsMultipleDays
                                }
                                onSelectDock={(dock) => {
                                    formRef.current?.submit({
                                        dockId: dock.id,
                                    });
                                }}
                                onPreview={preview.start}
                                onPreviewComplete={preview.cancel}
                            />

                            <UncaughtErrors
                                errors={errors}
                                exclude={[
                                    'username',
                                    'password',
                                    'requestedPickupDate',
                                    'requestedReturnDate',
                                    'couponCode',
                                    'customerTypeId',
                                ]}
                            />
                        </Stack>
                    </FormDimmableCard>
                </CardStack>
            </ScrollToLastMounted>
        </Form>
    );
};

DockView.layout = (page: ReactNode) => (
    <BookingFormLayout titleTranslationKey="CustomerArea.BookingForm.Dock.Title">
        <ComposeBookingLayout currentStep="dock">{page}</ComposeBookingLayout>
    </BookingFormLayout>
);

export default DockView;
